import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Search, Plus, Phone, Mail, Calendar, Clock, DollarSign, MapPin, User, ExternalLink, Filter, Eye, EyeOff, Layers, FileText, Briefcase, LayoutGrid, Wifi, WifiOff } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { filterJobsByPermissions } from '../../utils/permissions';
import { 
  BOARD_FLOWS, 
  BOARD_STORAGE_KEYS, 
  ENTITY_TYPES,
  getFlowStages,
  getFlowStageMapping,
  getNormalizedStage
} from '../../config/boardFlowsConfig';
import { FullLeadFlowModal } from '../../components/board/FullLeadFlowModal';
import { useBoardWebSocket } from '../../hooks/useBoardWebSocket';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Flow options for dropdown
const FLOW_OPTIONS = [
  { id: 'call_center', name: 'Call Center', icon: Phone },
  { id: 'sales', name: 'Sales', icon: Briefcase },
  { id: 'operations', name: 'Operations', icon: Layers },
  { id: 'full_flow', name: 'Full Lead Flow', icon: Layers },
];

// Entity type options
const ENTITY_OPTIONS = [
  { id: 'all', name: 'All Records', icon: LayoutGrid },
  { id: 'leads', name: 'Leads', icon: FileText },
  { id: 'projects', name: 'Projects', icon: Briefcase },
];

// Sales Board specific stages WITH new tracking stages
const SALES_STAGES = [
  { id: 'lead_issued', name: 'Lead Issued', color: 'bg-blue-100 dark:bg-blue-900/30', description: 'Leads issued to sales (auto-assigned to Stephen)' },
  { id: 'lead_assigned', name: 'Lead Assigned', color: 'bg-blue-100 dark:bg-blue-900/30', description: 'New leads assigned to sales' },
  { id: 'demo_confirmed', name: 'Demo – Confirmed', color: 'bg-indigo-100 dark:bg-indigo-900/30', description: 'Demo appointment scheduled' },
  { id: 'demo_no_sale', name: 'Demo – No Sale', color: 'bg-slate-200 dark:bg-slate-800', description: 'Demo completed, no sale', isArchive: true },
  { id: 'demo_sold', name: 'Demo – Sold', color: 'bg-emerald-100 dark:bg-emerald-900/30', description: 'Sale closed!' },
  // NEW: Sales tracking stages (shadow cards)
  { id: 'sold', name: 'Sold', color: 'bg-green-100 dark:bg-green-900/30', description: 'Sales shadow tracking - Sold', isShadow: true },
  { id: 'completed', name: 'Completed', color: 'bg-teal-100 dark:bg-teal-900/30', description: 'Completed (current year)', isShadow: true },
  { id: 'archived', name: 'Archived', color: 'bg-gray-100 dark:bg-gray-800', description: 'Archived (prior years)', isShadow: true, isArchive: true },
];

// Map existing stages to sales board stages
// Respects BP status parity - maps bp_status_name to internal workflow stages
const mapToSalesStage = (record) => {
  // For shadow cards (from sales_pipeline_items), use their tracking stage directly
  if (record.isShadowCard) {
    return record.sales_tracking_stage || 'sold';
  }
  
  // Check bp_status_name first, then fall back to status field
  const bpStatus = record.bp_status_name || record.status || '';
  const stage = record.stage || record.status || 'lead_assigned';
  
  // BP Status → Sales Stage mapping (preserves bp_status_name, maps for UI only)
  const bpStatusMapping = {
    // Lead Issued → Lead Issued (auto-assigned to Stephen)
    'Lead Issued': 'lead_issued',
    
    // Appointment stages → Demo Confirmed
    'Appointment Set': 'demo_confirmed',
    'Appointment Confirmed': 'demo_confirmed',
    'Lead Self-Booked': 'demo_confirmed',
    'Reset - w/ Appointment': 'reset_with_appointment',
    'Reset - Needs Appointment': 'reset_needs_appointment',
    'Reset - Appt Cancelled': 'reset_appt_cancelled',
    
    // No sale outcomes
    'Demo / No Sale': 'demo_no_sale',
    'No Demo': 'no_demo',
    'Demo': 'demo',
    'Appointment Cancelled': 'demo_no_sale',
    
    // Project sales stages (from jobs)
    'Estimate Request': 'estimate_request',
    'Estimate Sent': 'estimate_sent',
    'Customer Signed': 'customer_signed',
    'Sale Lost': 'sale_lost',
    'Sale Contingent': 'sale_contingent',
    'Cancelled': 'cancelled',
    'Sold': 'sold',
    
    // Lead sold stages
    'Job Sold': 'job_sold',
    'Job In Progress': 'job_in_progress',
    'Customer': 'customer',
    'Credit Reject': 'credit_reject',
    'Job Sold & Cancelled': 'job_sold_cancelled',
  };
  
  // Check BP status first
  if (bpStatus && bpStatusMapping[bpStatus]) {
    return bpStatusMapping[bpStatus];
  }
  
  // Fallback to stage-based mapping
  const stageMapping = {
    // Lead stages → Lead Issued
    'lead': 'lead_issued',
    'new': 'lead_issued',
    'lead_assigned': 'lead_issued',
    'lead_received': 'lead_issued',
    'lead_contacted': 'lead_issued',
    'lead_issued': 'lead_issued',
    'intro_call': 'lead_issued',
    'qualified': 'lead_issued',
    
    // Appointment/Demo stages
    'appointment_set': 'demo',
    'appointment_confirmed': 'demo',
    'demo': 'demo',
    'demo_confirmed': 'demo',
    'demo_scheduled': 'demo',
    'pending_signature': 'customer_signed',
    
    // No sale stages
    'demo_no_sale': 'demo_no_sale',
    'lost': 'sale_lost',
    'lost_dnq': 'sale_lost',
    'no_sale': 'demo_no_sale',
    
    // Project stages
    'estimate_request': 'estimate_request',
    'estimate_sent': 'estimate_sent',
    'customer_signed': 'customer_signed',
    
    // Sold stages
    'sold': 'sold',
    'job_sold': 'job_sold',
    'demo_sold': 'job_sold',
    'signed': 'customer_signed',
    'signed_agreement': 'customer_signed',
    'contract_signed': 'customer_signed',
    'customer': 'customer',
  };
  
  return stageMapping[stage] || 'unmapped';
};

export const SalesBoard = () => {
  const navigate = useNavigate();
  const { user, viewAsRole, viewAsUser } = useAuth();
  const [records, setRecords] = useState([]);
  const [allRecords, setAllRecords] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAssignedTo, setFilterAssignedTo] = useState('all');
  const [filterTeam, setFilterTeam] = useState('all');
  const [filterMarket, setFilterMarket] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [showNoSale, setShowNoSale] = useState(false);
  const [draggedCard, setDraggedCard] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [recordToConvert, setRecordToConvert] = useState(null);
  const [allFetchedData, setAllFetchedData] = useState(null);
  
  // NEW: Flow and Entity Type selectors with localStorage persistence
  const [flowType, setFlowType] = useState(() => {
    return localStorage.getItem(BOARD_STORAGE_KEYS.FLOW_TYPE) || 'sales';
  });
  const [entityType, setEntityType] = useState(() => {
    return localStorage.getItem(BOARD_STORAGE_KEYS.ENTITY_TYPE) || 'all';
  });
  const [showFullFlowModal, setShowFullFlowModal] = useState(false);

  // Real-time WebSocket for live updates
  const handleRealtimeUpdate = useCallback((data, updateType, board) => {
    console.log(`[SalesBoard] Real-time update: ${updateType}`, data?.id);
    // Trigger a full refresh for any update
    fetchData();
  }, []);

  const { isConnected: wsConnected } = useBoardWebSocket(flowType === 'operations' ? 'operations' : 'sales', {
    onLeadUpdate: handleRealtimeUpdate,
    onProjectUpdate: handleRealtimeUpdate,
    onAssignmentChange: handleRealtimeUpdate,
    onBulkUpdate: () => fetchData(),
    enabled: true,
  });

  // Update records when flowType changes
  useEffect(() => {
    if (allFetchedData) {
      let newRecords;
      if (flowType === 'operations') {
        newRecords = allFetchedData.opsJobs;
        console.log(`Switched to Operations: ${newRecords.length} jobs`);
      } else if (flowType === 'call_center') {
        newRecords = allFetchedData.allLeads;
        console.log(`Switched to Call Center: ${newRecords.length} leads`);
      } else {
        // Sales flow
        newRecords = [...allFetchedData.salesLeads, ...allFetchedData.salesJobs, ...allFetchedData.shadowCards];
        console.log(`Switched to Sales: ${newRecords.length} records`);
      }
      setAllRecords(newRecords);
    }
  }, [flowType, allFetchedData]);

  // Persist flow type to localStorage
  const handleFlowTypeChange = (newFlow) => {
    if (newFlow === 'full_flow') {
      setShowFullFlowModal(true);
    } else {
      setFlowType(newFlow);
      localStorage.setItem(BOARD_STORAGE_KEYS.FLOW_TYPE, newFlow);
    }
  };

  // Persist entity type to localStorage
  const handleEntityTypeChange = (newEntity) => {
    setEntityType(newEntity);
    localStorage.setItem(BOARD_STORAGE_KEYS.ENTITY_TYPE, newEntity);
  };

  // Get active stages based on selected flow
  const getActiveStages = () => {
    const flowConfig = BOARD_FLOWS[flowType];
    if (flowConfig && flowConfig.stages) {
      return flowConfig.stages;
    }
    return SALES_STAGES; // Fallback to sales stages
  };

  // Get stage mapping based on selected flow
  const getActiveStageMapping = () => {
    return getFlowStageMapping(flowType) || {};
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [jobsRes, leadsRes, usersRes, teamsRes, marketsRes, shadowCardsRes] = await Promise.all([
        axios.get(`${API}/jobs?limit=5000`),
        axios.get(`${API}/leads?limit=10000`).catch(() => ({ data: [] })),
        axios.get(`${API}/users`),
        axios.get(`${API}/teams`),
        axios.get(`${API}/markets`),
        // NEW: Fetch sales pipeline shadow cards
        axios.get(`${API}/sales-workflow/pipeline-items?include_archived=false`).catch(() => ({ data: { items: [] } }))
      ]);
      
      console.log(`Board: Fetched ${leadsRes.data?.length || 0} leads, ${jobsRes.data?.length || 0} jobs`);
      
      // Combine jobs and leads, marking their type
      const allJobs = (jobsRes.data || []).map(j => ({ 
        ...j, 
        recordType: 'project',
        customer_name: j.customer_name || j.job_name || 'Unknown Project',
      }));
      const allLeads = (leadsRes.data || []).map(l => ({ 
        ...l, 
        recordType: 'lead',
        customer_name: `${l.first_name || ''} ${l.last_name || ''}`.trim() || l.email,
        customer_phone: l.phone,
        customer_email: l.email,
        address: l.address || '',
      }));
      
      // NEW: Process shadow cards from sales pipeline
      const shadowCards = (shadowCardsRes.data?.items || []).map(s => ({
        ...s,
        isShadowCard: true,
        recordType: 'shadow',
        stage: s.sales_tracking_stage, // sold, completed, archived
      }));
      
      // Sales-relevant BP statuses (leads)
      const salesLeadStatuses = [
        'Lead Issued', 'Reset - Needs Appointment', 'Reset - w/ Appointment', 'Reset - Appt Cancelled',
        'No Demo', 'Demo', 'Demo / No Sale', 'Estimate Sent', 'Job Sold', 'Job In Progress', 'Customer',
        'Credit Reject', 'Job Sold & Cancelled'
      ];
      
      // Sales-relevant BP statuses (projects)
      const salesProjectStatuses = [
        'Estimate Request', 'Estimate Sent', 'Customer Signed', 'Sale Lost', 'Sold', 
        'Sale Contingent', 'Cancelled'
      ];
      
      // Ops-relevant BP statuses (projects only)
      const opsProjectStatuses = [
        'Sold', 'sold', 'Deposit Received', 'Sale Contingent', 'Contacted',
        'Scheduled', 'scheduled', 'In Progress', 'active',
        'Collections (Complete, Need Payment)', 'Complete', 'Recoating',
        'Cancelled', 'Sale Lost', 'None', 'appointment_set'
      ];
      
      // Filter leads for sales flow
      const salesLeads = allLeads.filter(record => {
        const bpStatus = record.bp_status_name || record.status || '';
        return salesLeadStatuses.includes(bpStatus);
      });
      
      // Filter jobs for sales flow
      const salesJobs = allJobs.filter(record => {
        const bpStatus = record.bp_status_name || record.status || '';
        return salesProjectStatuses.includes(bpStatus);
      });
      
      // Filter jobs for ops flow
      const opsJobs = allJobs.filter(record => {
        const bpStatus = record.bp_status_name || record.status || '';
        return opsProjectStatuses.includes(bpStatus);
      });
      
      // Store all data for flow switching
      setAllFetchedData({
        allLeads,
        allJobs,
        shadowCards,
        salesLeads,
        salesJobs,
        opsJobs
      });
      
      // Initial load - respect saved flow type from localStorage
      const savedFlow = localStorage.getItem(BOARD_STORAGE_KEYS.FLOW_TYPE) || 'sales';
      let initialRecords;
      if (savedFlow === 'operations') {
        initialRecords = opsJobs;
      } else if (savedFlow === 'call_center') {
        initialRecords = allLeads;
      } else {
        initialRecords = [...salesLeads, ...salesJobs, ...shadowCards];
      }
      
      setAllRecords(initialRecords);
      setUsers(usersRes.data);
      setTeams(teamsRes.data);
      setMarkets(marketsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load board data');
    } finally {
      setLoading(false);
    }
  };

  const applyRoleBasedFilter = useCallback(() => {
    if (!allRecords || allRecords.length === 0) {
      setRecords([]);
      return;
    }

    let effectiveUser = user;
    if (viewAsUser && viewAsRole) {
      const testUser = users.find(u => u.id === viewAsUser);
      if (testUser) {
        effectiveUser = testUser;
      }
    }

    // Filter jobs by permissions (leads and shadow cards pass through)
    const filteredRecords = allRecords.filter(record => {
      // Shadow cards always pass through for Sales visibility
      if (record.isShadowCard || record.recordType === 'shadow') return true;
      if (record.recordType === 'lead') return true;
      return filterJobsByPermissions([record], effectiveUser, users).length > 0;
    });
    
    setRecords(filteredRecords);
  }, [allRecords, user, viewAsUser, viewAsRole, users]);

  useEffect(() => {
    applyRoleBasedFilter();
  }, [applyRoleBasedFilter]);

  // Navigate to workspace when card is clicked
  const handleCardClick = (record) => {
    // Use entityType from dropdown to determine navigation
    // For 'all' mode, use the record's actual type to determine the path
    let path;
    if (entityType === 'all') {
      // Use record's actual type for navigation
      path = ENTITY_TYPES.all.detailPath(record.id, record.recordType);
    } else {
      path = ENTITY_TYPES[entityType]?.detailPath(record.id);
    }
    
    if (path) {
      navigate(path);
    } else {
      // Fallback to old behavior if config not found
      // view=detail hides the list sidebar when coming from board
      const mode = record.recordType === 'lead' ? 'leads' : 'projects';
      navigate(`/workspace/records?source=sales&mode=${mode}&id=${record.id}&view=detail`);
    }
  };

  const handleDragStart = (e, record) => {
    setDraggedCard(record);
    setIsDragging(true);
    setDragPosition({ x: e.clientX, y: e.clientY });
    e.dataTransfer.effectAllowed = 'move';
    
    const transparentImg = document.createElement('img');
    transparentImg.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    e.dataTransfer.setDragImage(transparentImg, 0, 0);
    
    const handleMouseMove = (evt) => {
      evt.preventDefault();
      setDragPosition({ x: evt.clientX, y: evt.clientY });
    };
    
    document.addEventListener('dragover', handleMouseMove);
    window._dragCleanup = () => {
      document.removeEventListener('dragover', handleMouseMove);
    };
  };

  const handleDragEnd = () => {
    if (window._dragCleanup) {
      window._dragCleanup();
      window._dragCleanup = null;
    }
    setIsDragging(false);
    setDraggedCard(null);
    setDragOverStage(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (stageId) => {
    setDragOverStage(stageId);
  };

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    
    if (!draggedCard) {
      handleDragEnd();
      return;
    }
    
    const currentFlowStage = mapToCurrentFlowStage(draggedCard);
    if (currentFlowStage === targetStage) {
      handleDragEnd();
      return;
    }

    const movedRecord = draggedCard;
    handleDragEnd();

    // Special handling for Demo – Sold (conversion) - only in sales flow
    if (flowType === 'sales' && targetStage === 'demo_sold' && movedRecord.recordType === 'lead') {
      setRecordToConvert(movedRecord);
      setConvertDialogOpen(true);
      return;
    }

    // Dynamic backend stage mapping based on current flow
    // For most stages, we use the stage ID directly as the backend value
    // Some stages need special mapping to match backend expectations
    const getBackendStage = (flowType, stageId) => {
      // Call Center specific mappings
      if (flowType === 'call_center') {
        const callCenterMapping = {
          'new': 'new',
          'lead_received': 'lead_received',
          'lead_contacted': 'contacted',
          'callback_scheduled': 'callback',
          'appointment_set': 'appointment_set',
          'appointment_confirmed': 'appointment_confirmed',
          'transferred_to_sales': 'qualified',
          'no_contact': 'no_contact',
        };
        return callCenterMapping[stageId] || stageId;
      }
      
      // Sales specific mappings
      if (flowType === 'sales') {
        const salesMapping = {
          'lead_assigned': 'lead',
          'demo_scheduled': 'appointment_set',
          'demo_confirmed': 'demo',
          'demo_completed': 'demo_completed',
          'proposal_sent': 'pending_signature',
          'demo_sold': 'sold',
          'to_production': 'production',
          'demo_no_sale': 'lost_dnq',
        };
        return salesMapping[stageId] || stageId;
      }
      
      // Operations specific mappings
      if (flowType === 'operations') {
        const opsMapping = {
          'production': 'production',
          'scheduled': 'scheduled',
          'crew_assigned': 'crew_assigned',
          'in_progress': 'install',
          'install_complete': 'install_complete',
          'qc': 'qc',
          'invoiced': 'invoice',
          'pending_payment': 'pending_payment',
          'paid': 'paid',
        };
        return opsMapping[stageId] || stageId;
      }
      
      return stageId;
    };

    const newBackendStage = getBackendStage(flowType, targetStage);
    const endpoint = movedRecord.recordType === 'lead' 
      ? `${API}/leads/${movedRecord.id}`
      : `${API}/jobs/${movedRecord.id}`;

    // Optimistic update
    setRecords(prev => prev.map(r => 
      r.id === movedRecord.id 
        ? { ...r, stage: newBackendStage, status: newBackendStage }
        : r
    ));
    setAllRecords(prev => prev.map(r => 
      r.id === movedRecord.id 
        ? { ...r, stage: newBackendStage, status: newBackendStage }
        : r
    ));

    try {
      const updateData = movedRecord.recordType === 'lead'
        ? { status: newBackendStage }
        : { stage: newBackendStage, status: newBackendStage };
      
      await axios.patch(endpoint, updateData);
      
      // Use dynamic flow stages instead of static SALES_STAGES
      const activeStages = getActiveStages();
      const stageName = activeStages.find(s => s.id === targetStage)?.name || targetStage;
      toast.success(`Moved to ${stageName}`);
      
      // If moved to production, refresh to update visibility
      if (targetStage === 'production' || targetStage === 'to_production') {
        toast.info('Record is now visible in Operations Board');
      }
    } catch (error) {
      // Revert on error
      setRecords(prev => prev.map(r => 
        r.id === movedRecord.id ? movedRecord : r
      ));
      setAllRecords(prev => prev.map(r => 
        r.id === movedRecord.id ? movedRecord : r
      ));
      console.error('Update stage error:', error);
      toast.error('Failed to update stage');
    }
  };

  const handleConvertToProject = async () => {
    if (!recordToConvert) return;

    try {
      // Create a new job from the lead
      const jobData = {
        customer_name: recordToConvert.customer_name,
        customer_email: recordToConvert.customer_email,
        customer_phone: recordToConvert.customer_phone,
        address: recordToConvert.address,
        city: recordToConvert.city,
        state: recordToConvert.state,
        zip_code: recordToConvert.zip_code,
        estimated_value: recordToConvert.estimated_value,
        priority: recordToConvert.priority || 'medium',
        assigned_to: recordToConvert.assigned_to,
        market_id: recordToConvert.market_id,
        stage: 'sold',
        status: 'sold',
        lead_id: recordToConvert.id,
        source: 'lead_conversion',
      };

      await axios.post(`${API}/jobs`, jobData);
      
      // Update the lead status to converted
      await axios.patch(`${API}/leads/${recordToConvert.id}`, { status: 'converted' });
      
      toast.success('Lead converted to Project successfully!');
      setConvertDialogOpen(false);
      setRecordToConvert(null);
      
      // Refresh data
      fetchData();
    } catch (error) {
      console.error('Conversion error:', error);
      toast.error('Failed to convert lead to project');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-400';
    }
  };

  const getDaysInStage = (record) => {
    const dateField = record.stage_entered_at || record.updated_at || record.created_at;
    if (!dateField) return 0;
    const entered = new Date(dateField);
    const now = new Date();
    return Math.floor((now - entered) / (1000 * 60 * 60 * 24));
  };

  const filteredRecords = records.filter(record => {
    // Entity type filter - filter by leads, projects, or show all
    if (entityType === 'leads' && record.recordType !== 'lead') return false;
    if (entityType === 'projects' && record.recordType !== 'project') return false;
    // 'all' shows both leads and projects
    
    // Search filter
    const search = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || 
      record.customer_name?.toLowerCase().includes(search) ||
      record.customer_email?.toLowerCase().includes(search) ||
      record.customer_phone?.includes(search) ||
      record.address?.toLowerCase().includes(search) ||
      record.first_name?.toLowerCase().includes(search) ||
      record.last_name?.toLowerCase().includes(search);
    
    const matchesAssignedTo = filterAssignedTo === 'all' || record.assigned_to === filterAssignedTo;
    const matchesTeam = filterTeam === 'all' || record.assigned_team === filterTeam;
    const matchesMarket = filterMarket === 'all' || record.market_id === filterMarket;
    const matchesPriority = filterPriority === 'all' || record.priority === filterPriority;
    
    return matchesSearch && matchesAssignedTo && matchesTeam && matchesMarket && matchesPriority;
  });

  // Get stages for current flow with archive filtering
  const activeStages = getActiveStages();
  const visibleStages = showNoSale 
    ? activeStages 
    : activeStages.filter(s => !s.isArchive);

  // Map record to current flow's stage
  const mapToCurrentFlowStage = (record) => {
    // For shadow cards, use their sales_tracking_stage directly
    // Shadow cards have stages: sold, completed, archived
    if (record.isShadowCard || record.recordType === 'shadow') {
      return record.sales_tracking_stage || record.stage || 'sold';
    }
    
    // For Sales flow, use mapToSalesStage which respects bp_status_name
    if (flowType === 'sales') {
      return mapToSalesStage(record);
    }
    
    // For other flows, use the bp_status_name first, then fall back to stage
    const bpStatus = record.bp_status_name || '';
    const stage = record.stage || record.status || 'new';
    const mapping = getActiveStageMapping();
    
    // First try to map bp_status_name to a stage
    const bpMapping = flowType === 'call_center' 
      ? {
          'Lead Received': 'lead_received',
          'Lead Contacted': 'lead_contacted',
          'Lead Self-Booked': 'lead_self_booked',
          'Lead Needs Self-Book Reschedule': 'lead_needs_self_book',
          'Appointment Not Set': 'appointment_not_set',
          'Appointment Set': 'appointment_set',
          'Appointment Confirmed': 'appointment_confirmed',
          'Appointment Cancelled': 'appointment_cancelled',
          'Nurture - Events': 'nurture',
          'Existing Customer': 'existing_customer',
        }
      : flowType === 'operations'
      ? {
          'Sold': 'sold',
          'sold': 'sold',
          'Deposit Received': 'deposit_received',
          'Sale Contingent': 'sale_contingent',
          'Contacted': 'contacted',
          'Scheduled': 'scheduled',
          'scheduled': 'scheduled',
          'In Progress': 'in_progress',
          'active': 'in_progress',
          'Collections (Complete, Need Payment)': 'collections',
          'Complete': 'complete',
          'Recoating': 'recoating',
          'Sale Lost': 'sale_lost',
          'Cancelled': 'cancelled',
          'None': 'sold',
          'appointment_set': 'scheduled',
          'Job In Progress': 'job_in_progress',
          'Customer': 'customer',
        }
      : {};
    
    if (bpStatus && bpMapping[bpStatus]) {
      return bpMapping[bpStatus];
    }
    
    // Fall back to stage-based mapping
    // mapping[stage] returns the stage object, we need to return the stage id
    if (mapping[stage]) {
      return mapping[stage].id || stage;
    }
    
    // Check if stage matches a stage ID directly in active stages
    const activeStageIds = activeStages.map(s => s.id);
    if (activeStageIds.includes(stage)) {
      return stage;
    }
    
    // Fallback to first stage of current flow
    return activeStages[0]?.id || 'lead_assigned';
  };

  // Get records for a specific stage in current flow
  const getRecordsByStage = (stageId) => {
    return filteredRecords.filter(record => {
      const mappedStage = mapToCurrentFlowStage(record);
      return mappedStage === stageId;
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading sales board...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4">
        {/* Header with Flow and Entity Selectors */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-bold">{BOARD_FLOWS[flowType]?.name || 'Sales'} Board</h1>
                {/* Real-time connection indicator */}
                <div 
                  className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
                    wsConnected ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}
                  title={wsConnected ? 'Real-time updates active' : 'Connecting...'}
                >
                  {wsConnected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
                  <span>{wsConnected ? 'Live' : 'Offline'}</span>
                </div>
              </div>
              <p className="text-muted-foreground">
                {BOARD_FLOWS[flowType]?.description || 'Track leads through your sales workflow'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3 flex-wrap">
            {/* Flow Type Selector */}
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium whitespace-nowrap">Flow:</Label>
              <Select value={flowType} onValueChange={handleFlowTypeChange}>
                <SelectTrigger className="w-[160px] h-9" data-testid="flow-type-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FLOW_OPTIONS.map(flow => (
                    <SelectItem key={flow.id} value={flow.id}>
                      <div className="flex items-center gap-2">
                        <flow.icon className="h-4 w-4" />
                        <span>{flow.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Entity Type Selector */}
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium whitespace-nowrap">View:</Label>
              <Select value={entityType} onValueChange={handleEntityTypeChange}>
                <SelectTrigger className="w-[130px] h-9" data-testid="entity-type-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ENTITY_OPTIONS.map(entity => (
                    <SelectItem key={entity.id} value={entity.id}>
                      <div className="flex items-center gap-2">
                        <entity.icon className="h-4 w-4" />
                        <span>{entity.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="h-6 w-px bg-border" />
            
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowNoSale(!showNoSale)}
              className={showNoSale ? 'bg-slate-100' : ''}
            >
              {showNoSale ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
              {showNoSale ? 'Hide Archive' : 'Show Archive'}
            </Button>
            <Button 
              onClick={() => navigate('/workspace/records?source=sales&mode=leads')} 
              variant="outline"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Open Workspace
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className={`text-white ${
          flowType === 'call_center' ? 'bg-gradient-to-r from-slate-600 to-slate-700' :
          flowType === 'operations' ? 'bg-gradient-to-r from-amber-600 to-orange-600' :
          'bg-gradient-to-r from-blue-600 to-indigo-600'
        }`}>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="md:col-span-1">
                <Label className="text-xs text-white/80">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/60" />
                  <Input
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 h-9 bg-white/10 border-white/20 text-white placeholder:text-white/60"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-xs text-white/80">Assigned To</Label>
                <Select value={filterAssignedTo} onValueChange={setFilterAssignedTo}>
                  <SelectTrigger className="h-9 bg-white/10 border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.first_name} {u.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-white/80">Team</Label>
                <Select value={filterTeam} onValueChange={setFilterTeam}>
                  <SelectTrigger className="h-9 bg-white/10 border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Teams</SelectItem>
                    {teams.map(team => (
                      <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-white/80">Market</Label>
                <Select value={filterMarket} onValueChange={setFilterMarket}>
                  <SelectTrigger className="h-9 bg-white/10 border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Markets</SelectItem>
                    {markets.map(market => (
                      <SelectItem key={market.id} value={market.id}>{market.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-white/80">Priority</Label>
                <Select value={filterPriority} onValueChange={setFilterPriority}>
                  <SelectTrigger className="h-9 bg-white/10 border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priorities</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Board */}
        <div className="overflow-x-auto pb-4">
          <div className="inline-flex gap-4 min-w-full">
            {visibleStages.map((stage) => {
              const stageRecords = getRecordsByStage(stage.id);
              
              // Get refined colors for columns
              const getColumnStyle = (stageColor) => {
                const colorMap = {
                  'bg-blue-100 dark:bg-blue-900/30': { border: 'border-l-blue-500', bg: 'bg-blue-50/70 dark:bg-blue-900/20', text: 'text-blue-700 dark:text-blue-300' },
                  'bg-indigo-100 dark:bg-indigo-900/30': { border: 'border-l-indigo-500', bg: 'bg-indigo-50/70 dark:bg-indigo-900/20', text: 'text-indigo-700 dark:text-indigo-300' },
                  'bg-slate-200 dark:bg-slate-800': { border: 'border-l-slate-400', bg: 'bg-slate-50/70 dark:bg-slate-800/50', text: 'text-slate-600 dark:text-slate-300' },
                  'bg-emerald-100 dark:bg-emerald-900/30': { border: 'border-l-emerald-500', bg: 'bg-emerald-50/70 dark:bg-emerald-900/20', text: 'text-emerald-700 dark:text-emerald-300' },
                  'bg-green-100 dark:bg-green-900/30': { border: 'border-l-green-500', bg: 'bg-green-50/70 dark:bg-green-900/20', text: 'text-green-700 dark:text-green-300' },
                  'bg-teal-100 dark:bg-teal-900/30': { border: 'border-l-teal-500', bg: 'bg-teal-50/70 dark:bg-teal-900/20', text: 'text-teal-700 dark:text-teal-300' },
                  'bg-gray-100 dark:bg-gray-800': { border: 'border-l-gray-400', bg: 'bg-gray-50/70 dark:bg-gray-800/50', text: 'text-gray-600 dark:text-gray-300' },
                };
                return colorMap[stageColor] || { border: 'border-l-slate-400', bg: 'bg-slate-50/70 dark:bg-slate-800/50', text: 'text-slate-600 dark:text-slate-300' };
              };
              
              const colStyle = getColumnStyle(stage.color);
              
              return (
                <div 
                  key={stage.id}
                  data-testid={`board-column-${stage.id}`}
                  className={`flex-shrink-0 w-72 ${stage.isArchive ? 'opacity-70' : ''}`}
                  onDragOver={handleDragOver}
                  onDragEnter={() => handleDragEnter(stage.id)}
                  onDrop={(e) => handleDrop(e, stage.id)}
                >
                  <div className={`${colStyle.bg} border-l-4 ${colStyle.border} rounded-lg p-3 transition-all shadow-sm ${
                    dragOverStage === stage.id ? 'ring-2 ring-blue-500 shadow-lg scale-[1.02]' : ''
                  }`}>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className={`font-semibold text-sm ${colStyle.text}`}>{stage.name}</h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{stage.description}</p>
                      </div>
                      <Badge variant="outline" className={`text-xs font-bold ${colStyle.text} border-current`}>
                        {stageRecords.length}
                      </Badge>
                    </div>
                    
                    <div className="space-y-2 min-h-[400px] max-h-[600px] overflow-y-auto">
                      {stageRecords.map(record => {
                        // Get priority color for card accent
                        const priorityAccent = {
                          urgent: 'border-l-4 border-l-rose-500 bg-rose-50/30 dark:bg-rose-900/10',
                          high: 'border-l-4 border-l-red-500 bg-red-50/30 dark:bg-red-900/10',
                          medium: 'border-l-4 border-l-amber-500 bg-amber-50/20 dark:bg-amber-900/10',
                          low: 'border-l-4 border-l-emerald-500 bg-emerald-50/20 dark:bg-emerald-900/10',
                        }[record.priority] || 'border-l-4 border-l-slate-300';
                        
                        // Value color
                        const getValueColor = (val) => {
                          if (!val) return 'text-slate-500';
                          if (val >= 20000) return 'text-emerald-600 dark:text-emerald-400 font-bold';
                          if (val >= 10000) return 'text-blue-600 dark:text-blue-400 font-semibold';
                          return 'text-slate-600 dark:text-slate-400 font-medium';
                        };
                        
                        return (
                        <div
                          key={record.id}
                          data-testid={`board-card-${record.id}`}
                          draggable
                          onDragStart={(e) => handleDragStart(e, record)}
                          onDragEnd={handleDragEnd}
                          onClick={() => handleCardClick(record)}
                          className={`bg-white dark:bg-slate-900 rounded-lg p-3 shadow-sm cursor-grab border border-slate-200 dark:border-slate-700 transition-all ${priorityAccent} ${
                            draggedCard?.id === record.id 
                              ? 'opacity-40 border-dashed cursor-grabbing' 
                              : 'hover:shadow-md hover:border-blue-300 dark:hover:border-blue-600 hover:-translate-y-0.5'
                          }`}
                        >
                          <div className="space-y-2">
                            {/* Header with name and priority */}
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-sm truncate text-slate-800 dark:text-slate-100">{record.customer_name}</h4>
                                {record.recordType === 'lead' && (
                                  <Badge variant="outline" className="text-[10px] mt-1 border-blue-300 text-blue-600 dark:text-blue-400">Lead</Badge>
                                )}
                                {record.isShadowCard && (
                                  <Badge className="text-[10px] mt-1 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300 border-0">Shadow</Badge>
                                )}
                              </div>
                              <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${getPriorityColor(record.priority)}`} 
                                   title={record.priority} />
                            </div>
                            
                            {/* Address */}
                            {record.address && (
                              <div className="flex items-start gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                                <MapPin className="h-3 w-3 mt-0.5 flex-shrink-0 text-slate-400" />
                                <span className="line-clamp-1">{record.address}</span>
                              </div>
                            )}
                            
                            {/* Value */}
                            {record.estimated_value && (
                              <div className="flex items-center gap-1.5 text-xs">
                                <DollarSign className="h-3 w-3 text-emerald-500" />
                                <span className={getValueColor(record.estimated_value)}>
                                  ${record.estimated_value.toLocaleString()}
                                </span>
                              </div>
                            )}
                            
                            {/* Demo/Install date */}
                            {(record.demo_date || record.appointment_date) && (
                              <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                                <Calendar className="h-3 w-3 text-slate-400" />
                                <span>
                                  {record.demo_date 
                                    ? `Demo: ${new Date(record.demo_date).toLocaleDateString()}`
                                    : `Appt: ${new Date(record.appointment_date).toLocaleDateString()}`
                                  }
                                </span>
                              </div>
                            )}
                            
                            {/* Footer */}
                            <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-800">
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Clock className="h-3 w-3" />
                                <span>{getDaysInStage(record)}d</span>
                              </div>
                              <div className="flex items-center gap-1">
                                {record.customer_phone && (
                                  <button 
                                    className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      window.location.href = `tel:${record.customer_phone}`;
                                    }}
                                  >
                                    <Phone className="h-3 w-3 text-slate-500" />
                                  </button>
                                )}
                                {record.customer_email && (
                                  <button 
                                    className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      window.location.href = `mailto:${record.customer_email}`;
                                    }}
                                  >
                                    <Mail className="h-3 w-3 text-slate-500" />
                                  </button>
                                )}
                                <ExternalLink className="h-3 w-3 text-blue-500 ml-1" />
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                      })}
                      
                      {stageRecords.length === 0 && (
                        <div className="text-center py-8 text-muted-foreground text-sm">
                          No records in this stage
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Floating drag preview */}
        {isDragging && draggedCard && (
          <div 
            className="fixed pointer-events-none z-50 bg-white dark:bg-slate-900 rounded-lg p-3 shadow-2xl border-2 border-blue-500 w-64 opacity-90"
            style={{
              left: dragPosition.x + 10,
              top: dragPosition.y + 10,
            }}
          >
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${getPriorityColor(draggedCard.priority)}`} />
              <span className="font-semibold text-sm truncate">{draggedCard.customer_name}</span>
            </div>
            {draggedCard.address && (
              <p className="text-xs text-muted-foreground mt-1 truncate">{draggedCard.address}</p>
            )}
          </div>
        )}

        {/* Convert to Project Dialog */}
        <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Convert Lead to Project</DialogTitle>
              <DialogDescription>
                Moving this lead to &quot;Demo – Sold&quot; will convert it to a Project.
              </DialogDescription>
            </DialogHeader>
            {recordToConvert && (
              <div className="py-4 space-y-3">
                <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
                  <h4 className="font-semibold">{recordToConvert.customer_name}</h4>
                  {recordToConvert.address && (
                    <p className="text-sm text-muted-foreground">{recordToConvert.address}</p>
                  )}
                  {recordToConvert.estimated_value && (
                    <p className="text-sm font-medium text-emerald-600 mt-2">
                      Est. Value: ${recordToConvert.estimated_value.toLocaleString()}
                    </p>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  The new Project will be placed in the &quot;Demo – Sold&quot; stage and will become visible to Operations when moved to Production.
                </p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setConvertDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleConvertToProject} className="bg-emerald-600 hover:bg-emerald-700">
                Convert to Project
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Full Lead Flow Modal */}
        <FullLeadFlowModal
          open={showFullFlowModal}
          onClose={() => setShowFullFlowModal(false)}
          entityType={entityType}
        />
      </div>
    </Layout>
  );
};

export default SalesBoard;
